import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { AnimatePresence, motion } from 'framer-motion';
import { useCuestionarios } from './hooks/useCuestionarios';
import { useCuestionarioForm } from './hooks/useCuestionarioForm';
import { TIPOS_PREGUNTA } from './constants';
import Loader from '../../components/UI/Loader';
import Alert from '../../components/UI/Alert';
import { toast } from 'react-toastify';

export default function CuestionarioEditorPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { detalle, crear, actualizar } = useCuestionarios();
  const {
    formData, loadFromApi, updateField,
    addPregunta, updatePregunta, removePregunta,
    addOpcion, updateOpcion, removeOpcion,
  } = useCuestionarioForm();

  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!!id);
  const [formError, setFormError] = useState(null);

  useEffect(() => {
    if (id) {
      detalle(id)
        .then(({ data }) => {
          loadFromApi(data);
          setLoading(false);
        })
        .catch((err) => {
          console.error('Error al cargar detalle', err);
          setLoading(false);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleSave = async () => {
    setFormError(null);
    if (!formData.nombre.trim()) {
      setFormError('El nombre del formulario es obligatorio.');
      return;
    }

    for (let i = 0; i < formData.preguntas.length; i++) {
      const p = formData.preguntas[i];
      if (!p.pregunta.trim()) {
        setFormError(`El texto de la pregunta #${i + 1} no puede estar en blanco.`);
        return;
      }
      if (p.tipo_pregunta === 'SELECCION') {
        if (p.opciones.length < 2) {
          setFormError(`La pregunta #${i + 1} de tipo selección debe tener al menos 2 opciones.`);
          return;
        }
        for (let j = 0; j < p.opciones.length; j++) {
          if (!p.opciones[j].opcion_texto.trim()) {
            setFormError(`La opción #${j + 1} de la pregunta #${i + 1} no puede estar en blanco.`);
            return;
          }
        }
      }
    }

    try {
      setSaving(true);
      if (id) {
        await actualizar(id, formData);
      } else {
        await crear(formData);
      }
      toast.success(id ? 'Formulario actualizado exitosamente' : 'Formulario creado exitosamente');
      navigate('/cuestionarios');
    } catch (err) {
      console.error('Error al guardar:', err.response?.data || err);
      setFormError('Error al guardar. Verifica los campos e intenta nuevamente.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="card p-0 radius-12">
        <div className="card-body p-24 d-flex align-items-center justify-content-center" style={{ minHeight: '300px' }}>
          <Loader type="spinner" size="lg" delay={0} center />
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-3">
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
          <div className="d-flex align-items-center gap-3">
            <button
              type="button"
              className="btn btn-sm btn-secondary-light d-flex align-items-center justify-content-center"
              onClick={() => navigate('/cuestionarios')}
              style={{ width: '40px', height: '40px', borderRadius: '50%' }}
            >
              <Icon icon="mdi:arrow-left" width="20" />
            </button>
            <div>
              <h5 className="mb-0 fw-semibold">
                {id ? 'Editar Información Adicional' : 'Nueva Información Adicional'}
              </h5>
              <span className="text-secondary-light text-sm">
                {id ? 'Modifica la estructura de este formulario' : 'Crea un nuevo formulario dinámico'}
              </span>
            </div>
          </div>

          <div className="d-flex gap-2">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => navigate('/cuestionarios')}
              disabled={saving}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="btn btn-primary d-flex align-items-center gap-2"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? (
                <>
                  <Loader type="spinner" size="sm" delay={0} />
                  Guardando...
                </>
              ) : (
                <>
                  <Icon icon="mdi:content-save" width="20" />
                  {id ? 'Actualizar' : 'Guardar'}
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {formError && (
        <Alert message={formError} variant="error" onClose={() => setFormError(null)} className="mb-3" />
      )}

      <div className="row g-4">
        {/* Columna izquierda: Configuración general */}
        <div className="col-lg-4">
          <div className="card p-0 radius-12">
            <div className="card-body p-24">
              <h6 className="fw-semibold mb-4 d-flex align-items-center gap-2 text-primary-light">
                <Icon icon="mdi:tune" width="18" />
                Configuración General
              </h6>

              <div className="mb-3">
                <label className="form-label fw-medium">
                  Nombre del Formulario <span className="text-danger">*</span>
                </label>
                <div className="input-group">
                  <span className="input-group-text">
                    <Icon icon="mdi:file-document-outline" width="18" />
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Ej. Datos Personales"
                    value={formData.nombre}
                    onChange={(e) => updateField('nombre', e.target.value)}
                  />
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label fw-medium">Descripción</label>
                <textarea
                  className="form-control"
                  rows="3"
                  placeholder="Describe el propósito del formulario..."
                  value={formData.descripcion}
                  onChange={(e) => updateField('descripcion', e.target.value)}
                />
              </div>

              <div className="mb-3">
                <label className="form-label fw-medium">Orden de visualización</label>
                <div className="input-group">
                  <span className="input-group-text">
                    <Icon icon="mdi:sort-numeric-ascending" width="18" />
                  </span>
                  <input
                    type="number"
                    min={1}
                    className="form-control"
                    value={formData.orden}
                    onChange={(e) => updateField('orden', +e.target.value)}
                  />
                </div>
              </div>

              <div className="mb-3">
                <div className="form-check form-switch">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="activo"
                    checked={formData.activo}
                    onChange={() => updateField('activo', !formData.activo)}
                  />
                  <label className="form-check-label fw-medium" htmlFor="activo">
                    Formulario Activo
                  </label>
                </div>
                <small className="text-secondary-light">Visible para los usuarios</small>
              </div>
            </div>
          </div>
        </div>

        {/* Columna derecha: Preguntas */}
        <div className="col-lg-8">
          <div className="card p-0 radius-12">
            <div className="card-body p-24">
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h6 className="fw-semibold mb-0 text-primary-light d-flex align-items-center gap-2">
                  <Icon icon="mdi:help-circle-outline" width="18" />
                  Preguntas ({formData.preguntas.length})
                </h6>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary d-flex align-items-center gap-1"
                  onClick={addPregunta}
                >
                  <Icon icon="mdi:plus" width="16" />
                  Añadir Pregunta
                </button>
              </div>

              <AnimatePresence mode="popLayout">
                {formData.preguntas.length === 0 ? (
                  <div className="text-center py-5">
                    <Icon
                      icon="mdi:help-box-multiple-outline"
                      width="64"
                      className="text-secondary-light mb-3"
                    />
                    <p className="text-secondary-light mb-1 fw-medium">Sin preguntas</p>
                    <p className="text-secondary-light text-sm mb-3">
                      Comienza añadiendo preguntas al formulario
                    </p>
                    <button
                      type="button"
                      className="btn btn-primary btn-sm d-inline-flex align-items-center gap-1"
                      onClick={addPregunta}
                    >
                      <Icon icon="mdi:plus" width="16" />
                      Añadir primera pregunta
                    </button>
                  </div>
                ) : (
                  formData.preguntas.map((preg, pIdx) => (
                    <motion.div
                      key={preg.id || `new-${pIdx}`}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, x: -30 }}
                      transition={{ type: 'spring', stiffness: 300, damping: 25 }}
                      className="border radius-8 mb-3"
                    >
                      {/* Cabecera de la pregunta */}
                      <div
                        className="d-flex align-items-center gap-3 p-3"
                        style={{ borderBottom: '1px solid var(--neutral-200, #e5e7eb)' }}
                      >
                        <span
                          className="d-flex align-items-center justify-content-center fw-bold text-white bg-primary radius-8 flex-shrink-0"
                          style={{ width: '28px', height: '28px', fontSize: '0.85rem' }}
                        >
                          {pIdx + 1}
                        </span>
                        <input
                          type="text"
                          className="form-control border-0 shadow-none fw-medium px-0"
                          placeholder="Escribe la pregunta aquí..."
                          value={preg.pregunta}
                          onChange={(e) => updatePregunta(pIdx, 'pregunta', e.target.value)}
                        />
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger d-flex align-items-center justify-content-center flex-shrink-0"
                          style={{ width: '32px', height: '32px', borderRadius: '50%', padding: 0 }}
                          onClick={() => removePregunta(pIdx)}
                          title="Eliminar pregunta"
                        >
                          <Icon icon="mdi:trash-can-outline" width="16" />
                        </button>
                      </div>

                      {/* Cuerpo de la pregunta */}
                      <div className="p-3">
                        <div className="row g-3 mb-3">
                          <div className="col-md-6">
                            <label className="form-label fw-medium text-sm">
                              Tipo de Respuesta
                            </label>
                            <select
                              className="form-select"
                              value={preg.tipo_pregunta}
                              onChange={(e) => updatePregunta(pIdx, 'tipo_pregunta', e.target.value)}
                            >
                              {TIPOS_PREGUNTA.map((t) => (
                                <option key={t.value} value={t.value}>
                                  {t.label}
                                </option>
                              ))}
                            </select>
                          </div>

                          <div className="col-md-6 d-flex flex-column justify-content-end gap-2 pb-1">
                            <div className="form-check form-switch">
                              <input
                                type="checkbox"
                                className="form-check-input"
                                id={`obligatorio-${pIdx}`}
                                checked={preg.obligatorio}
                                onChange={() => updatePregunta(pIdx, 'obligatorio', !preg.obligatorio)}
                              />
                              <label className="form-check-label fw-medium" htmlFor={`obligatorio-${pIdx}`}>
                                Obligatoria
                              </label>
                            </div>

                            {preg.tipo_pregunta === 'SELECCION' && (
                              <div className="form-check form-switch">
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  id={`multi-${pIdx}`}
                                  checked={preg.multi_respuesta}
                                  onChange={() => updatePregunta(pIdx, 'multi_respuesta', !preg.multi_respuesta)}
                                />
                                <label className="form-check-label fw-medium" htmlFor={`multi-${pIdx}`}>
                                  Selección múltiple
                                </label>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Opciones de selección */}
                        {preg.tipo_pregunta === 'SELECCION' && (
                          <div className="border-top pt-3">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <label className="form-label fw-medium text-sm mb-0">
                                Opciones <span className="text-danger">*</span>
                              </label>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-primary d-flex align-items-center gap-1"
                                onClick={() => addOpcion(pIdx)}
                              >
                                <Icon icon="mdi:plus-circle" width="16" />
                                Añadir opción
                              </button>
                            </div>

                            <AnimatePresence>
                              {preg.opciones.map((opt, oIdx) => (
                                <motion.div
                                  key={opt.id || `new-opt-${oIdx}`}
                                  initial={{ opacity: 0, height: 0 }}
                                  animate={{ opacity: 1, height: 'auto' }}
                                  exit={{ opacity: 0, height: 0 }}
                                  className="d-flex align-items-center gap-2 mb-2"
                                >
                                  <div
                                    className="flex-shrink-0 border"
                                    style={{
                                      width: '18px', height: '18px',
                                      borderRadius: preg.multi_respuesta ? '4px' : '50%',
                                    }}
                                  />
                                  <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    placeholder={`Opción ${oIdx + 1}`}
                                    value={opt.opcion_texto}
                                    onChange={(e) => updateOpcion(pIdx, oIdx, 'opcion_texto', e.target.value)}
                                  />
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-danger flex-shrink-0"
                                    onClick={() => removeOpcion(pIdx, oIdx)}
                                    title="Eliminar opción"
                                  >
                                    <Icon icon="mdi:close" width="14" />
                                  </button>
                                </motion.div>
                              ))}
                            </AnimatePresence>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  ))
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
